import React from 'react';

const TechSection = () => {
  const techFeatures = [
    {
      icon: 'satellite_alt',
      title: 'Real-time tracking',
      description: 'Live telemetry and geofencing for total visibility of your entire workforce in transit.'
    },
    {
      icon: 'receipt_long',
      title: 'Automated billing',
      description: 'ERP-integrated invoicing with zero manual intervention, providing clear cost allocation by department.'
    },
    {
      icon: 'support_agent',
      title: '24/7 Priority Support',
      description: 'Dedicated corporate desk to handle dynamic schedule changes and emergency re-routing.'
    }
  ];

  return (
    <section className="py-[40px] bg-surface-container-low">
      <div className="max-w-[1400px] mx-auto px-[32px]">
        <div className="text-center mb-[48px] max-w-[800px] mx-auto">
          <h2 className="font-headline text-[36px] font-extrabold tracking-tight text-on-surface mb-[24px]">The Tech Behind the Travel</h2>
          <p className="text-on-surface-variant text-[18px] leading-relaxed">We leverage a proprietary mobility stack to ensure every trip is logged, tracked, and billed with surgical precision.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-[64px] items-center">
          <div>
            <div className="space-y-[24px]">
              {techFeatures.map((f, i) => (
                <div key={i} className="flex gap-[24px] items-start">
                  <div className="bg-surface-container-low p-[12px] rounded-[8px] text-primary">
                    <span className="material-symbols-outlined text-[30px]">{f.icon}</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-[18px] mb-[4px]">{f.title}</h4>
                    <p className="text-[14px] text-on-surface-variant">{f.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="aspect-square bg-primary-container rounded-[24px] overflow-hidden shadow-2xl">
              {/* Inline SVG tech network graphic */}
              <svg
                viewBox="0 0 400 400"
                className="absolute inset-[0px] w-full h-full"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <defs>
                  <radialGradient id="bgGlow" cx="50%" cy="50%" r="60%">
                    <stop offset="0%" stopColor="#23a5a1" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#23a5a1" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* Background glow */}
                <rect width="400" height="400" fill="url(#bgGlow)" />

                {/* Grid lines — vertical */}
                <line x1="50"  y1="0" x2="50"  y2="400" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="100" y1="0" x2="100" y2="400" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="150" y1="0" x2="150" y2="400" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="200" y1="0" x2="200" y2="400" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="250" y1="0" x2="250" y2="400" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="300" y1="0" x2="300" y2="400" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="350" y1="0" x2="350" y2="400" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>

                {/* Grid lines — horizontal */}
                <line x1="0" y1="50"  x2="400" y2="50"  stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="0" y1="100" x2="400" y2="100" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="0" y1="150" x2="400" y2="150" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="0" y1="200" x2="400" y2="200" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="0" y1="250" x2="400" y2="250" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="0" y1="300" x2="400" y2="300" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>
                <line x1="0" y1="350" x2="400" y2="350" stroke="#23a5a1" strokeOpacity="0.1" strokeWidth="1"/>

                {/* Grid intersection dots */}
                {[50,100,150,200,250,300,350].flatMap((x, xi) =>
                  [50,100,150,200,250,300,350].map((y, yi) =>
                    <circle key={`d-${xi}-${yi}`} cx={x} cy={y} r="1.5" fill="#23a5a1" opacity="0.2"/>
                  )
                )}

                {/* Connection paths between hubs */}
                <path d="M80,80 L200,120 L320,80" stroke="#23a5a1" strokeOpacity="0.35" strokeWidth="1.5" fill="none"/>
                <path d="M200,120 L200,200" stroke="#23a5a1" strokeOpacity="0.4" strokeWidth="1.5" fill="none"/>
                <path d="M200,200 L120,290" stroke="#23a5a1" strokeOpacity="0.25" strokeWidth="1.5" fill="none" strokeDasharray="5,4"/>
                <path d="M200,200 L290,295" stroke="#23a5a1" strokeOpacity="0.25" strokeWidth="1.5" fill="none" strokeDasharray="5,4"/>
                <path d="M80,80 L120,290" stroke="#23a5a1" strokeOpacity="0.12" strokeWidth="1" fill="none"/>
                <path d="M320,80 L290,295" stroke="#23a5a1" strokeOpacity="0.12" strokeWidth="1" fill="none"/>

                {/* Hidden motion paths for animated dots */}
                <path id="mp1" d="M80,80 L200,120 L320,80" fill="none"/>
                <path id="mp2" d="M120,290 L200,200 L200,120 L80,80" fill="none"/>
                <path id="mp3" d="M290,295 L200,200 L320,80" fill="none"/>

                {/* Animated data-flow particles */}
                <circle r="3.5" fill="#23a5a1" opacity="0.9">
                  <animateMotion dur="3s" repeatCount="indefinite" calcMode="linear">
                    <mpath href="#mp1"/>
                  </animateMotion>
                </circle>
                <circle r="3" fill="#128884" opacity="0.8">
                  <animateMotion dur="4.5s" repeatCount="indefinite" begin="1.5s" calcMode="linear">
                    <mpath href="#mp2"/>
                  </animateMotion>
                </circle>
                <circle r="2.5" fill="#23a5a1" opacity="0.75">
                  <animateMotion dur="3.5s" repeatCount="indefinite" begin="0.8s" calcMode="linear">
                    <mpath href="#mp3"/>
                  </animateMotion>
                </circle>

                {/* Hub node A — top-left (80, 80) */}
                <circle cx="80" cy="80" r="6" fill="none" stroke="#23a5a1" strokeWidth="1">
                  <animate attributeName="r" values="6;16;6" dur="2.5s" repeatCount="indefinite"/>
                  <animate attributeName="stroke-opacity" values="0.35;0;0.35" dur="2.5s" repeatCount="indefinite"/>
                </circle>
                <circle cx="80" cy="80" r="6" fill="#23a5a1" opacity="0.4"/>
                <circle cx="80" cy="80" r="3" fill="#23a5a1" opacity="0.95"/>

                {/* Hub node B — top-center (200, 120) */}
                <circle cx="200" cy="120" r="8" fill="none" stroke="#23a5a1" strokeWidth="1">
                  <animate attributeName="r" values="8;20;8" dur="3s" begin="0.8s" repeatCount="indefinite"/>
                  <animate attributeName="stroke-opacity" values="0.3;0;0.3" dur="3s" begin="0.8s" repeatCount="indefinite"/>
                </circle>
                <circle cx="200" cy="120" r="8" fill="#23a5a1" opacity="0.3"/>
                <circle cx="200" cy="120" r="4" fill="#23a5a1" opacity="0.85"/>

                {/* Hub node C — top-right (320, 80) */}
                <circle cx="320" cy="80" r="6" fill="none" stroke="#23a5a1" strokeWidth="1">
                  <animate attributeName="r" values="6;16;6" dur="2.8s" begin="1.2s" repeatCount="indefinite"/>
                  <animate attributeName="stroke-opacity" values="0.35;0;0.35" dur="2.8s" begin="1.2s" repeatCount="indefinite"/>
                </circle>
                <circle cx="320" cy="80" r="6" fill="#23a5a1" opacity="0.4"/>
                <circle cx="320" cy="80" r="3" fill="#23a5a1" opacity="0.95"/>

                {/* Main hub — center (200, 200) */}
                <circle cx="200" cy="200" r="10" fill="none" stroke="#23a5a1" strokeWidth="1.5">
                  <animate attributeName="r" values="10;28;10" dur="2s" repeatCount="indefinite"/>
                  <animate attributeName="stroke-opacity" values="0.35;0;0.35" dur="2s" repeatCount="indefinite"/>
                </circle>
                <circle cx="200" cy="200" r="14" fill="#23a5a1" opacity="0.15"/>
                <circle cx="200" cy="200" r="8"  fill="#23a5a1" opacity="0.35"/>
                <circle cx="200" cy="200" r="4"  fill="#23a5a1" opacity="0.95"/>

                {/* Hub node D — bottom-left (120, 290) */}
                <circle cx="120" cy="290" r="5" fill="none" stroke="#23a5a1" strokeWidth="1">
                  <animate attributeName="r" values="5;13;5" dur="3.2s" begin="0.5s" repeatCount="indefinite"/>
                  <animate attributeName="stroke-opacity" values="0.3;0;0.3" dur="3.2s" begin="0.5s" repeatCount="indefinite"/>
                </circle>
                <circle cx="120" cy="290" r="5"   fill="#23a5a1" opacity="0.35"/>
                <circle cx="120" cy="290" r="2.5" fill="#23a5a1" opacity="0.9"/>

                {/* Hub node E — bottom-right (290, 295) */}
                <circle cx="290" cy="295" r="5" fill="none" stroke="#23a5a1" strokeWidth="1">
                  <animate attributeName="r" values="5;13;5" dur="2.7s" begin="1.8s" repeatCount="indefinite"/>
                  <animate attributeName="stroke-opacity" values="0.3;0;0.3" dur="2.7s" begin="1.8s" repeatCount="indefinite"/>
                </circle>
                <circle cx="290" cy="295" r="5"   fill="#23a5a1" opacity="0.35"/>
                <circle cx="290" cy="295" r="2.5" fill="#23a5a1" opacity="0.9"/>
              </svg>

              <div className="absolute inset-[0px] flex items-center justify-center">
                <div className="bg-background/95 p-[32px] rounded-[16px] shadow-xl border border-white/50 max-w-[320px] text-center">
                  <div className="text-[36px] font-extrabold text-primary mb-[8px]">99.9%</div>
                  <div className="text-[14px] font-bold uppercase tracking-widest text-on-surface-variant">System Uptime</div>
                  <div className="mt-[16px] pt-[16px] border-t border-outline-variant/30 text-[12px] text-on-surface-variant leading-tight">Our infrastructure is built for mission-critical corporate operations.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechSection;


